'use client';

/**
 * Site-wide top navigation — Industry design system (v3).
 *
 * Single hairline-ruled bar on the page ground: brand left, five section
 * links, the one solid primary button (Submit a project) and a quiet
 * Sign in link. Hidden on /portal, /vvb, /reviewer, /auth, /admin (those
 * routes carry their own authenticated chrome). On phone widths the links
 * collapse to a menu; brand + Submit stay.
 *
 * Brand + Submit + menu button need 403 px of viewport (measured 1 Oct 2026
 * in Chromium; the wordmark's tagline renders in the platform's system font,
 * so wider fonts need more — CI measured 30 px over at 360 px, 23 px here).
 * On narrower phones the menu button sat past the screen edge, cut off by the
 * overflow-x: clip on body. So below 420 px the wordmark stays whole (its
 * tagline was made legible at the owner's request, #215) and «Submit a
 * project» moves into the opened menu. The switch is CSS (.site-nav-cta /
 * .site-nav-menu-cta in globals.css), so desktop and tablet are untouched.
 *
 * Secondary routes (About, Marketplace, News, Contact, /verify, …) moved
 * to the footer with the redesign — the nav carries the five sections the
 * design names, nothing else.
 */
import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { MccLogo } from './MccLogo';

const NAV_ITEMS = [
  { href: '/', label: 'Programme' },
  { href: '/projects', label: 'Projects' },
  { href: '/methodologies', label: 'Standards' },
  { href: '/registry', label: 'Registry' },
  { href: '/governance', label: 'Governance' },
];

const HIDE_ON_PREFIXES = ['/portal', '/vvb', '/reviewer', '/auth', '/admin'];

export function SiteHeader() {
  const pathname = usePathname() || '/';
  const [isMobile, setIsMobile] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (HIDE_ON_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(href + '/');

  return (
    <nav
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'var(--color-bg)',
        borderBottom: '1px solid var(--color-divider)',
      }}
    >
      <div
        className="ind-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          height: isMobile ? 56 : 68,
        }}
      >
        <Link
          href="/"
          style={{ textDecoration: 'none', color: 'var(--color-text)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <MccLogo variant="inline" size={isMobile ? 22 : 28} />
        </Link>

        {!isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {NAV_ITEMS.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  className={active ? 'site-nav-link is-active' : 'site-nav-link'}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link href="/auth/login" className="site-nav-link site-nav-link--quiet" style={{ marginLeft: 12 }}>
              Sign in
            </Link>
            <Link href="/submit" className="btn btn-sm btn-primary" style={{ marginLeft: 12 }}>
              Submit a project
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Link href="/submit" className="btn btn-sm btn-primary site-nav-cta">
              Submit a project
            </Link>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              style={{ background: 'none', border: '1px solid var(--color-divider)', padding: 9, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}
            >
              <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--color-text)' }} />
              <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--color-text)', opacity: menuOpen ? 0 : 1 }} />
              <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--color-text)' }} />
            </button>
          </div>
        )}
      </div>

      {isMobile && menuOpen && (
        <div
          style={{
            background: 'var(--color-bg)',
            borderTop: '1px solid var(--color-divider)',
            padding: '4px 24px 16px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              style={{
                textDecoration: 'none',
                color: isActive(item.href) ? 'var(--color-accent-700)' : 'var(--color-text)',
                fontSize: 16,
                fontWeight: 500,
                padding: '10px 0',
                borderBottom: '1px solid var(--color-divider)',
              }}
            >
              {item.label}
            </Link>
          ))}
          <Link
            href="/auth/login"
            onClick={() => setMenuOpen(false)}
            style={{ textDecoration: 'none', color: 'var(--color-accent-700)', fontSize: 16, fontWeight: 500, padding: '10px 0' }}
          >
            Sign in
          </Link>
          <Link
            href="/submit"
            onClick={() => setMenuOpen(false)}
            className="btn btn-primary site-nav-menu-cta"
            style={{ alignSelf: 'flex-start', marginTop: 12 }}
          >
            Submit a project
          </Link>
        </div>
      )}
    </nav>
  );
}
